import { vars } from 'src/core/themes';
import { PresetDataPoint } from 'src/utils/strategyCalculator';

export const CURVE_COLOR = vars.color.textBrand;
export const CHART_BORDER_COLOR = vars.color.borderPrimary;
export const CHART_HEIGHT = 280;
/** Number of evenly spaced control-point dots to show on the curve */
export const CONTROL_POINT_COUNT = 8;

/** Convert daysToEvent to a Date timestamp relative to the event date */
export const daysToEventToTimestamp = (
  daysToEvent: number,
  eventDate: Date
): number => {
  const d = new Date(eventDate);
  d.setDate(d.getDate() - daysToEvent);
  return d.getTime();
};

/**
 * Filter data points to only those within [0, daysToEvent] and convert
 * to [timestamp, value][] for Highcharts. Sorted chronologically.
 */
export const toChartData = (
  data: PresetDataPoint[],
  daysToEvent: number,
  eventDate: Date
): [number, number][] =>
  data
    .filter((p) => p.daysToEvent >= 0 && p.daysToEvent <= daysToEvent)
    .sort((a, b) => a.daysToEvent - b.daysToEvent)
    .map((p) => [daysToEventToTimestamp(p.daysToEvent, eventDate), p.value]);

/**
 * Pick N evenly spaced dots by choosing the nearest actual data point
 * for each target timestamp. Because the spline passes through every
 * real data point, these dots always sit exactly on the rendered curve.
 */
export const pickControlPoints = (
  splineData: [number, number][],
  minTs: number,
  maxTs: number,
  count: number
): [number, number][] => {
  if (splineData.length === 0) return [];
  if (splineData.length <= count) return splineData;

  const picked: [number, number][] = [];
  const usedIndices = new Set<number>();

  for (let i = 0; i < count; i++) {
    const targetTs = minTs + (i / (count - 1)) * (maxTs - minTs);
    // Find the nearest data point not yet used
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let j = 0; j < splineData.length; j++) {
      if (usedIndices.has(j)) continue;
      const dist = Math.abs(splineData[j][0] - targetTs);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = j;
      }
    }
    usedIndices.add(bestIdx);
    picked.push(splineData[bestIdx]);
  }

  // Sort chronologically
  picked.sort((a, b) => a[0] - b[0]);
  return picked;
};
